import { Spacing } from '@persona-id/design-tokens';
import { FC, useState } from 'react';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableActionTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { CaseQuery } from 'dashboard/models/case-query';
import { CaseDataManagerSchema } from 'dashboard/models/case-template';
import {
  EXTERNAL_INTEGRATION_DISPLAY_NAME,
  ExternalIntegrationDataSource,
} from 'dashboard/models/external-integration';
import FormAddCaseQuery, {
  FormAddCaseQueryData,
} from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormAddCaseQuery';
import FormEditCaseQuery, {
  FormEditCaseQueryData,
} from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormEditCaseQuery';
import Button from 'design-system/components/Button';
import { Heading, Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;
`;

export interface CaseQueryTableProps {
  caseDataManagerSchema: CaseDataManagerSchema;
  caseQueries: CaseQuery[];
  dataSources: ExternalIntegrationDataSource[];
  handleCreateCaseQuery: (values: FormAddCaseQueryData) => Promise<void>;
  handleUpdateCaseQuery: (values: FormEditCaseQueryData, id: string) => Promise<void>;
}

const CaseQueryTable: FC<CaseQueryTableProps> = ({
  caseDataManagerSchema,
  caseQueries,
  dataSources,
  handleCreateCaseQuery,
  handleUpdateCaseQuery,
}: CaseQueryTableProps) => {
  const [selectedQueryId, setSelectedQueryId] = useState<string | undefined>(undefined);
  const selectedQuery = caseQueries.find((caseQuery) => caseQuery.id === selectedQueryId);

  const [addCaseQueryModal, { enable: showAddCaseQueryModal, disable: hideAddCaseQueryModal }] =
    useToggle(false);
  const [editCaseQueryModal, { enable: showEditCaseQueryModal, disable: hideEditCaseQueryModal }] =
    useToggle(false);

  const handleCreateCaseQueryAndClose = (values: FormAddCaseQueryData) => {
    handleCreateCaseQuery(values);
    hideAddCaseQueryModal();
  };

  const handleEditCaseQueryAndClose = (values: FormEditCaseQueryData) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleUpdateCaseQuery(values, selectedQueryId);
    hideEditCaseQueryModal();
  };

  // TODO: only show active case queries
  const caseQueryRows = caseQueries.map((caseQuery, i) => {
    const handleEditCaseQuery = () => {
      setSelectedQueryId(caseQuery.id);
      showEditCaseQueryModal();
    };

    return (
      <TableTR key={i}>
        <TableTD>{caseQuery.attributes.name}</TableTD>
        <TableTD>
          {caseQuery.attributes['query-type'] === 'datasource'
            ? EXTERNAL_INTEGRATION_DISPLAY_NAME[
                caseQuery.relationships['external-integration'].data.type
              ]
            : 'JS'}
        </TableTD>
        <TableActionTD>
          <Button
            id={`case-template-case-query-table__row--${caseQuery.id}__edit-query`}
            name={`case-template-case-query-table__row--${caseQuery.id}__edit-query`}
            variant='basic'
            type='button'
            onClick={handleEditCaseQuery}
          >
            Edit
          </Button>
        </TableActionTD>
      </TableTR>
    );
  });

  return (
    <>
      <TableTitleContainer>
        <div>
          <Heading size='sm'>Queries</Heading>
          <Paragraph>
            Configure queries to run on external data sources and show the insights on your cases.
          </Paragraph>
        </div>
        <ButtonGroup>
          <Button
            id='case-template-case-query-table__add-query-button'
            name='case-template-case-query-table__add-query-button'
            variant='basic'
            type='button'
            onClick={() => showAddCaseQueryModal()}
          >
            Add query
          </Button>
        </ButtonGroup>
      </TableTitleContainer>
      <TableLayout
        headerTR={
          <>
            <TableTH>Name</TableTH>
            <TableTH>Data source type</TableTH>
            <TableTH>{/* Actions column */}</TableTH>
          </>
        }
        isEmpty={caseQueries.length === 0}
        nullState={
          <TableNullState dataTestBlock='case-template-case-query-table__null-state'>
            <Paragraph>
              Looks like there aren&#39;t any query configured for this case template yet.
            </Paragraph>
          </TableNullState>
        }
      >
        {caseQueryRows}
      </TableLayout>
      <ModalWrapper isOpen={addCaseQueryModal} handleCloseModal={hideAddCaseQueryModal}>
        <ModalContainer>
          <ModalHeader onClose={hideAddCaseQueryModal}>Add query</ModalHeader>
          <ModalBody>
            <FormAddCaseQuery
              caseQueries={caseQueries}
              dataSources={dataSources}
              caseDataManagerSchema={caseDataManagerSchema}
              submit={handleCreateCaseQueryAndClose}
              handleClose={hideAddCaseQueryModal}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={editCaseQueryModal} handleCloseModal={hideEditCaseQueryModal}>
        <ModalContainer>
          <ModalHeader onClose={hideEditCaseQueryModal}>Edit query</ModalHeader>
          <ModalBody>
            <FormEditCaseQuery
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              caseQuery={selectedQuery}
              allCaseQueries={caseQueries}
              caseDataManagerSchema={caseDataManagerSchema}
              dataSources={dataSources}
              submit={handleEditCaseQueryAndClose}
              handleClose={hideEditCaseQueryModal}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default CaseQueryTable;
